import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { BACKEND_URL } from "../api";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Map, AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_MAP_ID, TAFT_CENTER, googleMapsDirectionsUrl, useGoogleMapsStatus } from "../components/GoogleMapsProvider";
import ServiceDaysStrip from "../components/ServiceDaysStrip";
import TabletDriverHub, { DriverMessagesSheet } from "../components/driver/TabletDriverHub";
import { NavigateButton, DriverSettingsSheet, DriverDisposalSheet } from "../components/driver/NavigationLauncher";
import { getCurrentPosition as driverGetCurrentPosition, DriverGeoError } from "../lib/driverGeo";
import {
  Truck, NavigationArrow, CheckCircle, Warning, Camera, X, SignOut, Phone, MapPin,
  CaretLeft, CaretRight, Lock, MapTrifold, Crosshair, CaretDown, CaretUp, Trash, ArrowsClockwise,
  Play, Flag, DeviceMobile, DesktopTower, Gear, Prohibit, Receipt,
} from "@phosphor-icons/react";

const ALLOWED_PHOTO_EXT = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
const MAX_PHOTO_BYTES = 25 * 1024 * 1024;
const MAX_ISSUE_PHOTOS = 10;
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,image/*";
const photoSrc = (url) => (url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

const ISSUES = [
  { v: "blocked_container", l: "Blocked Container" },
  { v: "contaminated_load", l: "Contaminated Load" },
  { v: "overloaded_dumpster", l: "Overloaded Dumpster" },
  { v: "gate_locked", l: "Gate Locked" },
  { v: "cannot_access", l: "Cannot Access" },
  { v: "other", l: "Other Issue" },
];
const issueLabel = (v) => ISSUES.find((i) => i.v === v)?.l || (v || "").replace(/_/g, " ");

// Human-readable frequency label combining `service_frequency` and the
// count of selected `service_days`. Prevents the "biweekly Wed" trap
// where days.length=1 alone would incorrectly render "1x/Week".
function frequencyLabel(stop) {
  const days = (stop.service_days || []).length;
  if (days === 0) return "—";
  const freq = stop.service_frequency || "weekly";
  if (freq === "biweekly")     return days === 1 ? "Bi-Weekly" : `Bi-Weekly · ${days}x`;
  if (freq === "monthly")      return days === 1 ? "Monthly"   : `Monthly · ${days}x`;
  if (freq === "twice_weekly") return "2x/Week";
  if (freq === "three_weekly") return "3x/Week";
  return days === 1 ? "Weekly" : `${days}x/Week`;
}

export default function DriverApp() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ route: null, stops: [], completions: [] });
  const [idx, setIdx] = useState(0);
  const [issueOpen, setIssueOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [driverPos, setDriverPos] = useState(null); // [lat, lng]
  const [geoErr, setGeoErr] = useState("");
  const watchIdRef = useRef(null);
  const [issueType, setIssueType] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]); // [{key, local, url, pct, status: "uploading"|"done"}]
  const [photoError, setPhotoError] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [completedOpen, setCompletedOpen] = useState(false);
  const photosRef = useRef([]);
  photosRef.current = photos;
  const uploading = photos.some((p) => p.status === "uploading");

  // Route session (Start / Finish Route)
  const [sessionBusy, setSessionBusy] = useState(false);
  const [elapsedTick, setElapsedTick] = useState(0); // forces re-render every 30s for the running clock

  // Driver chooses route + service_date before viewing stops.
  // Persisted to localStorage so phone refresh / app re-open keeps context.
  const [selection, setSelection] = useState(() => {
    try {
      const raw = localStorage.getItem("zmex.driver.selection");
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (obj?.routeId && obj?.serviceDate) return obj;
    } catch { /* ignore */ }
    return null;
  });
  const [routeOptions, setRouteOptions] = useState(null); // null = not loaded yet

  // Driver UI mode — "phone" preserves the existing mobile-first layout,
  // "tablet" switches to the truck-mount cockpit (`TabletDriverHub`).
  // Default = tablet on wide screens (≥1024px), phone otherwise; persisted
  // to localStorage.
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem("zmex.driver.mode");
      if (saved === "phone" || saved === "tablet") return saved;
    } catch { /* ignore */ }
    if (typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)").matches) {
      return "tablet";
    }
    return "phone";
  });
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [disposalOpen, setDisposalOpen] = useState(false);
  // When a driver explicitly clicks "Change Route" from inside the hub, we
  // re-show the selector but disable autoConfirm so they can actually pick
  // a different route/date instead of being re-locked instantly.
  const [reselecting, setReselecting] = useState(false);

  function switchMode(next) {
    setMode(next);
    try { localStorage.setItem("zmex.driver.mode", next); } catch { /* ignore */ }
  }

  async function loadRouteOptions() {
    try {
      const r = await api.get("/driver/routes");
      setRouteOptions(r.data);
    } catch (err) {
      console.warn("Failed to load driver routes", err);
      setRouteOptions({ routes: [], today: null, service_date: null });
    }
  }

  async function load() {
    if (!selection) {
      await loadRouteOptions();
      return;
    }
    const r = await api.get("/driver/today", {
      params: { route_id: selection.routeId, service_date: selection.serviceDate },
    });
    setData(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selection?.routeId, selection?.serviceDate]);

  function commitSelection(routeId, serviceDate) {
    const next = { routeId, serviceDate };
    setSelection(next);
    setReselecting(false);
    try { localStorage.setItem("zmex.driver.selection", JSON.stringify(next)); } catch { /* ignore */ }
  }

  function resetSelection() {
    setSelection(null);
    setData({ route: null, stops: [], completions: [] });
    setIdx(0);
    setReselecting(true);
    try { localStorage.removeItem("zmex.driver.selection"); } catch { /* ignore */ }
    loadRouteOptions();
  }

  // Live geolocation watch while map is open (browser Geolocation API).
  useEffect(() => {
    async function stopWatch() {
      if (watchIdRef.current == null) return;
      try {
        if (navigator.geolocation) navigator.geolocation.clearWatch(watchIdRef.current);
      } catch { /* ignore */ }
      watchIdRef.current = null;
    }

    if (!mapOpen) { stopWatch(); return; }

    setGeoErr("");
    if (!navigator.geolocation) {
      setGeoErr("Geolocation not supported on this device");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => setDriverPos([pos.coords.latitude, pos.coords.longitude]),
      (err) => setGeoErr(err.message || "Unable to read location"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    return () => { stopWatch(); };
  }, [mapOpen]);

  const stops = data.stops || [];
  const completionMap = useMemo(
    () => Object.fromEntries((data.completions || []).map((c) => [c.container_id || c.customer_id, c])),
    [data]
  );

  // Pending = anything NOT marked completed OR acknowledged (no-pickup).
  // Issues stay visible so driver can resolve. Off-week / suspended stops
  // remain in the pending list too until the driver taps Acknowledge, which
  // records an "acknowledged" completion and drops them out.
  const pendingStops = useMemo(
    () => stops.filter((s) => {
      const st = completionMap[s.id]?.status;
      return st !== "completed" && st !== "acknowledged";
    }),
    [stops, completionMap]
  );
  const completedStops = useMemo(
    () => stops.filter((s) => {
      const st = completionMap[s.id]?.status;
      return st === "completed" || st === "acknowledged";
    }),
    [stops, completionMap]
  );

  // Clamp idx to pending list whenever it shrinks (after a completion)
  useEffect(() => {
    if (idx > 0 && idx >= pendingStops.length) {
      setIdx(Math.max(0, pendingStops.length - 1));
    }
  }, [pendingStops.length, idx]);

  const current = pendingStops[idx];
  const completedCount = completedStops.length;
  const issueCount = pendingStops.filter((s) => completionMap[s.id]?.status === "issue").length;
  const progress = stops.length ? Math.round((completedCount / stops.length) * 100) : 0;

  // Latest known GPS — used to stamp completions and issues
  const lastGpsRef = useRef(null);

  async function captureGps(timeoutMs = 4000) {
    try {
      const p = await driverGetCurrentPosition({
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 30000,
        // Silent capture — we don't want to prompt for permission from every
        // Complete/Issue tap. The Settings gear + map "Enable Location" flow
        // is where the driver explicitly grants access.
        requestPermission: false,
      });
      const g = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
      lastGpsRef.current = g;
      return g;
    } catch (e) {
      // Fall back to last known if we can't fix now.
      if (!(e instanceof DriverGeoError)) console.warn("captureGps failed:", e);
      return lastGpsRef.current;
    }
  }

  // ---------- Nearest stop ----------
  const [nearestId, setNearestId] = useState("");          // container id of nearest pending stop
  const [nearestMeta, setNearestMeta] = useState(null);    // { distance_mi, missing_gps_count }
  const [findingNearest, setFindingNearest] = useState(false);

  function haversineMiles(a, b) {
    if (!a || !b) return null;
    const toRad = (d) => (d * Math.PI) / 180;
    const R_mi = 3958.7613;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R_mi * Math.asin(Math.min(1, Math.sqrt(x)));
  }

  async function findNearestStop() {
    if (findingNearest) return;
    if (!pendingStops.length) {
      toast.info("No pending stops on this route");
      return;
    }
    setFindingNearest(true);
    try {
      const gps = await captureGps(8000);
      if (!gps) {
        toast.error("Location access required — please enable GPS for ZMEX Hub");
        return;
      }
      // Filter pending stops that have valid coordinates
      const withCoords = pendingStops.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
      const missing = pendingStops.length - withCoords.length;
      if (!withCoords.length) {
        toast.error(`No pending stops have GPS coordinates${missing ? ` (${missing} missing)` : ""}`);
        return;
      }
      let best = null;
      let bestDist = Infinity;
      for (const s of withCoords) {
        const d = haversineMiles(gps, { lat: s.lat, lng: s.lng });
        if (d != null && d < bestDist) { bestDist = d; best = s; }
      }
      if (!best) {
        toast.error("Couldn't compute nearest stop");
        return;
      }
      setNearestId(best.id);
      setNearestMeta({ distance_mi: bestDist, missing_gps_count: missing });
      // Move it to the top of the list visually by setting idx to its current position
      const pos = pendingStops.findIndex((s) => s.id === best.id);
      if (pos >= 0) setIdx(pos);
      toast.success(`Nearest stop · ${best.business_name}`, {
        description: `${bestDist.toFixed(2)} mi away${missing ? ` · ${missing} stop${missing > 1 ? "s" : ""} missing GPS` : ""}`,
      });
    } finally {
      setFindingNearest(false);
    }
  }

  // Clear the nearest-stop highlight if that stop gets completed/issue-resolved
  useEffect(() => {
    if (!nearestId) return;
    const stillPending = pendingStops.some((s) => s.id === nearestId);
    if (!stillPending) { setNearestId(""); setNearestMeta(null); }
  }, [pendingStops, nearestId]);

  async function markComplete() {
    if (!current || !data.route) return;
    const gps = await captureGps();
    await api.post("/stops/complete", {
      route_id: data.route.id,
      customer_id: current.customer_id || current.id,
      container_id: current.container_id || current.id,
      status: "completed", notes: "",
      service_date: selection?.serviceDate || data?.service_date,
      gps_lat: gps?.lat, gps_lng: gps?.lng, gps_accuracy: gps?.accuracy,
    });
    toast.success(`✓ ${current.business_name} · ${current.waste_stream || "MSW"} completed`);
    setIdx(0);
    await load();
  }

  // Acknowledge a stop with NO pickup. Used for:
  //   - reason="off_week"   → bi-weekly rotation off-week (auto-advances anchor)
  //   - reason="suspended"  → customer suspended (no anchor change)
  async function acknowledgeStop(stop, reason) {
    if (!stop || !data.route) return;
    const gps = await captureGps();
    try {
      await api.post("/stops/complete", {
        route_id: data.route.id,
        customer_id: stop.customer_id || stop.id,
        container_id: stop.container_id || stop.id,
        status: "acknowledged",
        ack_reason: reason,
        notes: reason === "off_week" ? "Bi-weekly off-week acknowledged" : `Suspended · ${stop.suspension_reason || "reason not set"}`,
        service_date: selection?.serviceDate || data?.service_date,
        gps_lat: gps?.lat, gps_lng: gps?.lng, gps_accuracy: gps?.accuracy,
      });
      toast.success(
        reason === "off_week"
          ? `↻ ${stop.business_name} · Off-week acknowledged`
          : `⊘ ${stop.business_name} · Suspension acknowledged`
      );
      setIdx(0);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not acknowledge stop");
    }
  }

  async function startRoute() {
    if (!data.route || sessionBusy) return;
    setSessionBusy(true);
    try {
      const gps = await captureGps();
      await api.post("/routes/sessions/start", {
        route_id: data.route.id,
        service_date: selection?.serviceDate || data?.service_date,
        gps_lat: gps?.lat, gps_lng: gps?.lng, gps_accuracy: gps?.accuracy,
      });
      toast.success("Route started — clock running");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the route");
    } finally {
      setSessionBusy(false);
    }
  }

  async function finishRoute() {
    if (!data.route || sessionBusy) return;
    const pendingCount = pendingStops.length;
    if (pendingCount > 0) {
      const ok = window.confirm(
        `You still have ${pendingCount} active stop${pendingCount > 1 ? "s" : ""}. Finish route anyway?`
      );
      if (!ok) return;
    }
    setSessionBusy(true);
    try {
      const gps = await captureGps();
      const r = await api.post("/routes/sessions/finish", {
        route_id: data.route.id,
        service_date: selection?.serviceDate || data?.service_date,
        gps_lat: gps?.lat, gps_lng: gps?.lng, gps_accuracy: gps?.accuracy,
      });
      const hrs = Number(r.data?.total_hours || 0);
      toast.success(`✓ Route finished · ${hrs.toFixed(2)} hrs`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish the route");
    } finally {
      setSessionBusy(false);
    }
  }

  // Tick every second while a session is in progress so the elapsed clock updates live
  useEffect(() => {
    if (!data?.active_session?.start_time) return;
    const t = setInterval(() => setElapsedTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [data?.active_session?.start_time]);

  async function submitIssue() {
    if (!current || !data.route || !issueType) {
      toast.error("Select an issue type");
      return;
    }
    const photoUrls = photos.filter((p) => p.status === "done").map((p) => p.url);
    if (!photoUrls.length) {
      toast.error("Photo required when reporting an issue");
      return;
    }
    if (uploading) {
      toast.error("Wait for photo upload to finish");
      return;
    }
    const gps = await captureGps();
    try {
      await api.post("/stops/complete", {
        route_id: data.route.id,
        customer_id: current.customer_id || current.id,
        container_id: current.container_id || current.id,
        status: "issue",
        service_date: selection?.serviceDate || data?.service_date,
        issue_type: issueType, notes, photo_url: photoUrls[0], photo_urls: photoUrls,
        gps_lat: gps?.lat, gps_lng: gps?.lng, gps_accuracy: gps?.accuracy,
      });
      toast.success("Issue reported");
      closeIssueModal();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to report issue");
    }
  }

  function patchPhoto(key, patch) {
    setPhotos((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  }

  function removePhoto(key) {
    setPhotos((ps) => {
      const p = ps.find((x) => x.key === key);
      if (p?.local) { try { URL.revokeObjectURL(p.local); } catch { /* ignore */ } }
      return ps.filter((x) => x.key !== key);
    });
  }

  function clearPhotos() {
    photosRef.current.forEach((p) => { try { URL.revokeObjectURL(p.local); } catch { /* ignore */ } });
    setPhotos([]);
    setPhotoError("");
  }

  function closeIssueModal() {
    setIssueOpen(false);
    setIssueType("");
    setNotes("");
    clearPhotos();
  }

  async function uploadOne(file, key) {
    const fd = new FormData();
    fd.append("file", file);
    // Retry twice on network / server hiccups (weak cell signal in the field).
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await api.post("/upload-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) patchPhoto(key, { pct: Math.round((evt.loaded * 100) / evt.total) });
          },
        });
        patchPhoto(key, { url: r.data.url, pct: 100, status: "done" });
        return;
      } catch (err) {
        const status = err?.response?.status;
        if (attempt === 2 || (status && status < 500)) {
          const msg = err?.response?.data?.detail || "Upload failed — please retake the photo";
          setPhotoError(msg);
          toast.error(msg);
          removePhoto(key);
          return;
        }
        await new Promise((res) => setTimeout(res, 1500 * (attempt + 1)));
      }
    }
  }

  function onPhoto(e) {
    const files = Array.from(e.target.files || []);
    // Always reset the input so the same file can be re-selected after a remove
    e.target.value = "";
    if (!files.length) return;
    setPhotoError("");
    const room = MAX_ISSUE_PHOTOS - photosRef.current.length;
    if (files.length > room) toast.error(`Up to ${MAX_ISSUE_PHOTOS} photos per issue`);

    for (const file of files.slice(0, Math.max(0, room))) {
      const fname = (file.name || "").toLowerCase();
      const ext = fname.includes(".") ? fname.split(".").pop() : "";
      const ct = (file.type || "").toLowerCase();
      if (!(ct.startsWith("image/") || ALLOWED_PHOTO_EXT.includes(ext))) {
        const msg = "Only JPG, PNG, WebP, or HEIC images are allowed";
        setPhotoError(msg); toast.error(msg);
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        const msg = `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 25 MB.`;
        setPhotoError(msg); toast.error(msg);
        continue;
      }
      const key = `p_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      setPhotos((ps) => [...ps, { key, local: URL.createObjectURL(file), url: "", pct: 0, status: "uploading" }]);
      uploadOne(file, key);
    }
  }

  // Cleanup object URLs on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => clearPhotos(), []);

  function navigateTo(c) {
    if (!c) return;
    const q = c.lat && c.lng ? `${c.lat},${c.lng}` : encodeURIComponent(c.service_address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank");
  }

  async function doLogout() {
    try { await api.post("/driver/offline"); }
    catch (err) { console.warn("driver/offline failed:", err?.message || err); }
    await logout();
    nav("/login", { replace: true });
  }

  // Periodic GPS ping every 60s while a route is loaded
  useEffect(() => {
    if (!data?.route?.id) return;
    let cancelled = false;
    async function ping() {
      const g = await captureGps(8000);
      if (cancelled || !g) return;
      try {
        await api.post("/driver/ping", {
          lat: g.lat, lng: g.lng, accuracy: g.accuracy,
          route_id: data.route.id, status: "active",
        });
      } catch (err) {
        // Driver may be offline — surface to console but don't disrupt the route
        console.warn("driver/ping failed:", err?.message || err);
      }
    }
    ping(); // immediate
    const t = setInterval(ping, 60000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.route?.id]);

  // Route selection screen — shown whenever the driver hasn't picked a route+date yet.
  // Phone mode → full-page selector. Tablet mode → modal popup.
  // autoConfirm is suppressed when the driver explicitly re-opens the selector
  // from inside the hub via "Change Route" — otherwise it would re-lock instantly.
  if (!selection) {
    return (
      <RouteSelector
        user={user}
        options={routeOptions}
        variant={mode === "tablet" ? "modal" : "page"}
        autoConfirm={!reselecting}
        onPick={(routeId, serviceDate) => commitSelection(routeId, serviceDate)}
        onLogout={doLogout}
      />
    );
  }

  // ── TABLET MODE — truck-mount cockpit layout ──────────────
  if (mode === "tablet") {
    // Build a display-ready route object so the top bar always has something
    // meaningful — even when route.name is blank or the route has 0 stops today.
    const tabletRoute = (() => {
      if (data.route) {
        const r = data.route;
        const num = r.route_number ? `Route #${r.route_number}` : "Route";
        const composed = r.name ? `${num} · ${r.name}` : num;
        return { ...r, name: composed };
      }
      const opt = (routeOptions?.routes || []).find((r) => r.id === selection?.routeId);
      if (!opt) return null;
      return {
        id: opt.id,
        name: opt.route_name
          ? `Route #${opt.route_number} · ${opt.route_name}`
          : `Route #${opt.route_number}`,
      };
    })();
    return (
      <>
        <TabletDriverHub
          user={user}
          route={tabletRoute}
          serviceDate={selection.serviceDate}
          todayLabel={data?.today || formatDriverDate(selection.serviceDate)}
          stops={stops}
          pendingStops={pendingStops}
          completedStops={completedStops}
          completionMap={completionMap}
          current={current}
          idx={idx}
          onPickStop={(realIdx) => setIdx(realIdx)}
          completedCount={completedCount}
          issueCount={issueCount}
          progress={progress}
          activeSession={data?.active_session}
          lastSession={data?.last_session}
          sessionBusy={sessionBusy}
          elapsedTick={elapsedTick}
          onStart={startRoute}
          onFinish={finishRoute}
          onComplete={markComplete}
          onIssue={() => setIssueOpen(true)}
          onAcknowledge={acknowledgeStop}
          onNavigate={navigateTo}
          onCall={(phone) => { window.location.href = `tel:${phone}`; }}
          onFindNearest={findNearestStop}
          findingNearest={findingNearest}
          nearestId={nearestId}
          nearestMeta={nearestMeta}
          onClearNearest={() => { setNearestId(""); setNearestMeta(null); }}
          onOpenMap={() => setMapOpen(true)}
          onRefresh={async () => { await load(); toast.success("Route refreshed"); }}
          onOpenMessages={() => setMessagesOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenDisposal={() => setDisposalOpen(true)}
          onChangeRoute={resetSelection}
          onSwitchToPhone={() => switchMode("phone")}
          onLogout={doLogout}
        />

        {/* Messages placeholder */}
        <DriverMessagesSheet open={messagesOpen} onClose={() => setMessagesOpen(false)} />
        <DriverDisposalSheet
          open={disposalOpen}
          onClose={() => setDisposalOpen(false)}
          route={data.route}
          onSaved={async () => { toast.success("Disposal ticket submitted"); await load(); }}
        />
        <DriverSettingsSheet
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          route={data.route}
          stops={stops}
          completions={data.completions || []}
          onReloaded={load}
        />

        {/* Issue modal (shared with phone layout) */}
        {issueOpen && (
          <IssueReportModal
            current={current}
            issueType={issueType} setIssueType={setIssueType}
            notes={notes} setNotes={setNotes}
            photos={photos} photoError={photoError}
            onPhoto={onPhoto} removePhoto={removePhoto}
            onClose={closeIssueModal}
            onSubmit={submitIssue}
            onPreviewClick={(url) => setLightboxUrl(photoSrc(url))}
          />
        )}

        {/* Fullscreen photo lightbox */}
        {lightboxUrl && (
          <div className="fixed inset-0 z-[60] bg-black/85 grid place-items-center p-4" onClick={() => setLightboxUrl("")} data-testid="photo-lightbox">
            <img src={lightboxUrl} alt="Issue evidence" className="max-h-[90vh] max-w-[95vw] rounded-sm" />
            <button className="absolute top-4 right-4 p-2 bg-white/10 text-white rounded-sm" onClick={() => setLightboxUrl("")} aria-label="Close lightbox" data-testid="photo-lightbox-close">
              <X size={20} weight="bold" />
            </button>
          </div>
        )}

        {/* Route Map overlay (shared) */}
        {mapOpen && (
          <RouteMapOverlay
            stops={stops}
            completionMap={completionMap}
            driverPos={driverPos}
            geoErr={geoErr}
            activeStopId={current?.id}
            onClose={() => setMapOpen(false)}
            onPickStop={(id) => {
              const i = stops.findIndex((s) => s.id === id);
              if (i >= 0) setIdx(i);
              setMapOpen(false);
            }}
          />
        )}
      </>
    );
  }

  // ── PHONE MODE — existing mobile layout (unchanged) ───────
  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white pb-32" data-testid="driver-app">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#012340] text-white">
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-sm bg-[#CFAB7A] grid place-items-center text-[#012340] flex-shrink-0">
              <Truck size={20} weight="bold" />
            </div>
            <div className="min-w-0">
              <button
                onClick={resetSelection}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] hover:underline"
                data-testid="driver-change-route"
              >
                <CaretLeft size={10} weight="bold" /> Change route
              </button>
              <div className="font-heading font-bold truncate">{data.route?.name || (data.route ? `Route #${data.route.route_number}` : "No route")}</div>
              <div className="text-[10px] text-white/70 font-mono">{formatDriverDate(selection.serviceDate)} · {data?.today || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-2 rounded-sm hover:bg-white/10 text-white/70 hover:text-white"
              data-testid="driver-settings-btn"
              title="Driver settings"
            >
              <Gear size={18} weight="bold" />
            </button>
            <button
              onClick={() => switchMode("tablet")}
              className="hidden sm:inline-flex p-2 rounded-sm hover:bg-white/10 text-white/70 hover:text-white"
              data-testid="driver-switch-tablet"
              title="Switch to tablet layout"
            >
              <DesktopTower size={18} weight="bold" />
            </button>
            <button onClick={doLogout} className="p-2 rounded-sm hover:bg-white/10" data-testid="driver-logout">
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between text-xs">
            <div>Hi, <span className="font-bold">{user?.name}</span></div>
            <div className="text-[#CFAB7A] font-bold">{completedCount}/{stops.length} done · {progress}%{issueCount > 0 ? ` · ${issueCount} issue${issueCount > 1 ? "s" : ""}` : ""}</div>
          </div>
          <div className="mt-2 h-1.5 bg-white/15 rounded-full overflow-hidden">
            <div className="h-full bg-[#CFAB7A]" style={{ width: `${progress}%` }} />
          </div>
          {stops.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setMapOpen(true)}
                data-testid="open-route-map"
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] py-2.5 font-bold text-sm tracking-wide transition-all hover:brightness-105"
              >
                <MapTrifold size={18} weight="bold" /> Route Map
              </button>
              <button
                onClick={() => setDisposalOpen(true)}
                data-testid="open-disposal"
                className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-[#CFAB7A]/60 text-[#CFAB7A] py-2.5 font-bold text-sm tracking-wide transition-all hover:bg-[#CFAB7A]/10"
              >
                <Receipt size={18} weight="bold" /> Disposal
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Route session card — Start / Finish Route */}
      {data.route && (
        <RouteSessionCard
          activeSession={data.active_session}
          lastSession={data.last_session}
          busy={sessionBusy}
          onStart={startRoute}
          onFinish={finishRoute}
          elapsedTick={elapsedTick}
        />
      )}

      {!data.route && (
        <div className="p-6 text-center text-[#5C738A]">
          No route is assigned for today. Contact your dispatcher.
        </div>
      )}

      {/* Current stop */}
      {current && (
        <section className="p-4">
          <div className={`bg-[#0f1622] border rounded-sm overflow-hidden ${current.id === nearestId ? "border-[#CFAB7A] ring-1 ring-[#CFAB7A]/40" : "border-white/10"}`} data-testid="current-stop-card">
            <div className="bg-[#012340] text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="bg-[#CFAB7A] text-[#012340] w-7 h-7 grid place-items-center rounded-sm font-heading font-bold flex-shrink-0">{idx + 1}</span>
                <span className="text-xs uppercase tracking-[0.25em] text-[#CFAB7A]">Stop {idx + 1} of {pendingStops.length}</span>
                {current.id === nearestId && (
                  <span className="ml-1 text-[9px] font-black uppercase tracking-[0.18em] bg-[#CFAB7A] text-[#012340] px-1.5 py-0.5 rounded-sm whitespace-nowrap" data-testid="current-nearest-badge">
                    Nearest
                  </span>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} className="p-1.5 rounded-sm bg-white/10 disabled:opacity-30" data-testid="prev-stop"><CaretLeft size={16} weight="bold" /></button>
                <button onClick={() => setIdx(Math.min(pendingStops.length - 1, idx + 1))} disabled={idx >= pendingStops.length - 1} className="p-1.5 rounded-sm bg-white/10 disabled:opacity-30" data-testid="next-stop"><CaretRight size={16} weight="bold" /></button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="font-heading text-2xl font-black text-white">{current.business_name}</div>
                <div className="text-sm text-white/70 flex items-start gap-1.5 mt-1">
                  <MapPin size={16} weight="bold" className="mt-0.5 flex-shrink-0" />
                  <span>{current.service_address}</span>
                </div>
                {current.id === nearestId && nearestMeta && (
                  <div className="text-[11px] text-[#CFAB7A] font-mono tabular-nums mt-1.5">
                    📍 {nearestMeta.distance_mi.toFixed(2)} mi from your current location
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Pill label="Stream" value={current.waste_stream || "MSW"} />
                <Pill label="Size" value={`${current.container_size || current.dumpster_size} yd³${(current.quantity || 1) > 1 ? ` ×${current.quantity}` : ""}`} />
                <Pill label="Frequency" value={frequencyLabel(current)} data-testid="driver-frequency" />
              </div>
              <ServiceDaysStrip days={current.service_days || []} />
              {current.gate_code && (
                <div className="bg-[#CFAB7A]/15 border border-[#CFAB7A]/40 rounded-sm px-3 py-2 flex items-center gap-2">
                  <Lock size={16} weight="bold" className="text-[#CFAB7A]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CFAB7A]">Gate Code</span>
                  <span className="ml-auto font-heading font-bold text-white">{current.gate_code}</span>
                </div>
              )}
              {current.service_notes && (
                <div className="text-xs bg-white/5 border border-white/10 rounded-sm p-3">
                  <div className="font-bold uppercase tracking-[0.2em] text-[#CFAB7A] mb-1">Notes</div>
                  <div className="text-white/90">{current.service_notes}</div>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <NavigateButton stop={current} variant="compact" className="flex-1" />
                {current.phone && (
                  <a href={`tel:${current.phone}`} className="inline-flex items-center justify-center rounded-sm border border-white/15 px-4 py-3 text-white font-bold text-sm hover:bg-white/5" data-testid="call-btn">
                    <Phone size={18} weight="bold" />
                  </a>
                )}
              </div>

              {completionMap[current.id] ? (
                <div className={`text-sm rounded-sm px-3 py-2 ${
                  completionMap[current.id].status === "completed"
                    ? "bg-[#2E7D32]/15 text-[#7FC683] border border-[#2E7D32]/40"
                    : completionMap[current.id].status === "acknowledged"
                      ? (completionMap[current.id].ack_reason === "suspended"
                          ? "bg-[#D32F2F]/15 text-[#FF8A80] border border-[#D32F2F]/40"
                          : "bg-[#F57C00]/15 text-[#FFB877] border border-[#F57C00]/40")
                      : "bg-[#F57C00]/15 text-[#FFB877] border border-[#F57C00]/40"
                }`} data-testid="current-stop-status">
                  {completionMap[current.id].status === "completed"
                    ? "✓ Completed"
                    : completionMap[current.id].status === "acknowledged"
                      ? (completionMap[current.id].ack_reason === "suspended"
                          ? "⊘ Suspension acknowledged · no pickup"
                          : "↻ Off-week acknowledged · no pickup")
                      : `⚠ Issue: ${issueLabel(completionMap[current.id].issue_type)}`}
                </div>
              ) : current.suspended ? (
                /* RED · Suspended customer card ─────────────────────────── */
                <div className="rounded-sm border-2 border-[#D32F2F] bg-[#D32F2F]/[0.12] p-3 space-y-2.5" data-testid="suspended-panel">
                  <div className="flex items-center gap-2">
                    <Prohibit size={20} weight="bold" className="text-[#FF8A80] flex-shrink-0" />
                    <div className="text-xs font-black uppercase tracking-[0.22em] text-[#FF8A80]">
                      Suspended · {current.suspension_reason || "Payment"}
                    </div>
                  </div>
                  {current.suspension_note && (
                    <div className="text-xs text-white/85 italic bg-black/20 rounded-sm px-2 py-1.5">
                      "{current.suspension_note}"
                    </div>
                  )}
                  <div className="text-[11px] text-white/70">
                    No pickup required. Tap <b>Acknowledge</b> to log a no-service, or <b>Service Anyway</b> if the customer paid on the truck.
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => acknowledgeStop(current, "suspended")}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#D32F2F] text-white px-4 py-3 font-bold text-sm hover:brightness-110 transition"
                      data-testid="acknowledge-suspended-btn"
                    >
                      <Prohibit size={18} weight="bold" /> Acknowledge
                    </button>
                    <button
                      onClick={markComplete}
                      className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-white/40 text-white px-4 py-3 font-bold text-sm hover:bg-white/10"
                      data-testid="service-anyway-btn"
                    >
                      <CheckCircle size={18} weight="bold" /> Service Anyway
                    </button>
                  </div>
                </div>
              ) : current.rotation_status === "off_week" ? (
                /* AMBER · Bi-weekly off-week card ────────────────────────── */
                <div className="rounded-sm border-2 border-[#F57C00] bg-[#F57C00]/[0.10] p-3 space-y-2.5" data-testid="offweek-panel">
                  <div className="flex items-center gap-2">
                    <ArrowsClockwise size={20} weight="bold" className="text-[#FFB877] flex-shrink-0" />
                    <div className="text-xs font-black uppercase tracking-[0.22em] text-[#FFB877]">
                      Skip Today · Bi-Weekly Off Week
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-white/85">
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">Last serviced</div>
                      <div className="font-mono tabular-nums">{formatDriverDate(current.last_serviced_at) || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">Return next</div>
                      <div className="font-mono tabular-nums text-[#FFB877]">{formatDriverDate(current.next_pickup_date) || "—"}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => acknowledgeStop(current, "off_week")}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#F57C00] text-[#012340] px-4 py-3 font-bold text-sm hover:brightness-105 transition"
                      data-testid="acknowledge-offweek-btn"
                    >
                      <ArrowsClockwise size={18} weight="bold" /> Acknowledge
                    </button>
                    <button
                      onClick={markComplete}
                      className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-white/40 text-white px-4 py-3 font-bold text-sm hover:bg-white/10"
                      data-testid="force-pickup-btn"
                    >
                      <CheckCircle size={18} weight="bold" /> Force Pickup
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={markComplete} className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] px-4 py-3 font-bold text-sm hover:brightness-105 transition" data-testid="complete-btn">
                    <CheckCircle size={18} weight="bold" /> Complete Stop
                  </button>
                  <button onClick={() => setIssueOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-[#D32F2F] text-[#FF8A80] px-4 py-3 font-bold text-sm hover:bg-[#D32F2F]/15" data-testid="issue-btn">
                    <Warning size={18} weight="bold" /> Issue
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Active stops list (pending + unresolved issues) — Nearest button + scrollable list */}
      <section className="px-4" data-testid="active-stops-section">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CFAB7A] flex items-center gap-2">
            <span>Active Stops</span>
            <span className="text-white font-mono">{pendingStops.length}</span>
          </div>
          <button
            onClick={findNearestStop}
            disabled={findingNearest || pendingStops.length === 0}
            data-testid="nearest-stop-btn"
            className="inline-flex items-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] px-3.5 py-2 font-heading font-black text-xs tracking-wide disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-105 transition-all"
            aria-label="Find nearest stop"
          >
            <Crosshair size={14} weight="bold" />
            {findingNearest ? "Locating…" : "Nearest Stop"}
          </button>
        </div>

        {nearestId && nearestMeta && (
          <div
            className="mb-3 rounded-sm border-2 border-[#CFAB7A]/60 bg-[#CFAB7A]/[0.08] px-3 py-2 flex items-center justify-between gap-2"
            data-testid="nearest-stop-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Crosshair size={14} weight="fill" className="text-[#CFAB7A] flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#CFAB7A]">Nearest Stop</div>
                <div className="text-xs text-white font-mono tabular-nums">
                  {nearestMeta.distance_mi.toFixed(2)} mi from you
                  {nearestMeta.missing_gps_count > 0 && (
                    <span className="text-[#FFB877]"> · {nearestMeta.missing_gps_count} missing GPS</span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => { setNearestId(""); setNearestMeta(null); }}
              className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-white px-2 py-1"
              data-testid="nearest-stop-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        {pendingStops.length === 0 && stops.length > 0 && (
          <div className="bg-[#2E7D32]/15 border border-[#2E7D32]/40 rounded-sm px-4 py-6 text-center" data-testid="route-finished-banner">
            <CheckCircle size={28} weight="fill" className="text-[#7FC683] mx-auto mb-2" />
            <div className="font-heading font-black text-white">Route Complete</div>
            <div className="text-xs text-white/70 mt-1">All {stops.length} stops handled. Great work!</div>
          </div>
        )}

        {/* Scrollable stop list — sized to show ~7 cards at a time on mobile.
            Inner overflow keeps the Start/Finish/Nearest controls + current-stop card
            visible above without forcing the driver to scroll the whole page. */}
        {pendingStops.length > 0 && (
          <div
            className="overflow-y-auto pr-1 -mr-1 rounded-sm"
            style={{ maxHeight: "min(60vh, 560px)" }}
            data-testid="stop-list-scroll"
          >
            <div className="space-y-2 pb-1">
              {(() => {
                // Build display order: if a nearest stop is set, surface it first;
                // otherwise keep the original route order.
                const ordered = (() => {
                  if (!nearestId) return pendingStops;
                  const top = pendingStops.find((s) => s.id === nearestId);
                  const rest = pendingStops.filter((s) => s.id !== nearestId);
                  return top ? [top, ...rest] : pendingStops;
                })();
                return ordered.map((s) => {
                  const realIdx = pendingStops.findIndex((p) => p.id === s.id);
                  const c = completionMap[s.id];
                  const isNearest = s.id === nearestId;
                  const distance = isNearest && nearestMeta ? nearestMeta.distance_mi : null;
                  const isSuspended = !!s.suspended;
                  const isOffWeek = s.rotation_status === "off_week";
                  return (
                    <button
                      key={s.id}
                      onClick={() => setIdx(realIdx)}
                      data-testid={`stop-row-${s.id}`}
                      className={`w-full text-left bg-[#0f1622] border rounded-sm px-3 py-3 flex items-center gap-3 transition ${
                        isNearest
                          ? "border-[#CFAB7A] ring-1 ring-[#CFAB7A]/40"
                          : realIdx === idx
                            ? "border-[#CFAB7A]"
                            : isSuspended
                              ? "border-[#D32F2F]/60 hover:border-[#D32F2F]"
                              : isOffWeek
                                ? "border-[#F57C00]/60 hover:border-[#F57C00]"
                                : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-sm grid place-items-center font-heading font-bold text-sm flex-shrink-0 ${
                        c?.status === "issue"
                          ? "bg-[#F57C00] text-white"
                          : isSuspended
                            ? "bg-[#D32F2F] text-white"
                            : isOffWeek
                              ? "bg-[#F57C00]/70 text-white"
                              : "bg-[#CFAB7A] text-[#012340]"
                      }`}>
                        {c?.status === "issue" ? "!" : isSuspended ? "⊘" : isOffWeek ? "↻" : realIdx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-bold text-sm text-white truncate">{s.business_name}</div>
                          {isNearest && (
                            <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-[#CFAB7A] text-[#012340] px-1.5 py-0.5 rounded-sm">Nearest</span>
                          )}
                          {isSuspended && !isNearest && (
                            <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-[#D32F2F] text-white px-1.5 py-0.5 rounded-sm">Suspended</span>
                          )}
                          {isOffWeek && !isSuspended && !isNearest && (
                            <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-[#F57C00] text-[#012340] px-1.5 py-0.5 rounded-sm">Skip · Off Wk</span>
                          )}
                        </div>
                        <div className="text-xs text-white/60 truncate">{s.service_address}</div>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          <div className="max-w-[180px]">
                            <ServiceDaysStrip days={s.service_days || []} compact />
                          </div>
                          {distance != null && (
                            <span className="text-[10px] text-[#CFAB7A] font-mono tabular-nums">
                              {distance.toFixed(2)} mi
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-white/60 flex-shrink-0">
                        <div className="font-bold text-[#CFAB7A]">{s.waste_stream || "MSW"}</div>
                        <div>{s.container_size || s.dumpster_size}yd³</div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        )}
      </section>

      {/* Completed stops (separate, collapsible) */}
      {completedStops.length > 0 && (
        <section className="px-4 mt-6" data-testid="completed-stops-section">
          <button
            onClick={() => setCompletedOpen((o) => !o)}
            className="w-full flex items-center justify-between mb-2 text-white/60 hover:text-white"
            data-testid="completed-stops-toggle"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] flex items-center gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#7FC683]" />
              Completed Stops
              <span className="text-white font-mono">{completedStops.length}</span>
            </span>
            {completedOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          </button>
          {completedOpen && (
            <div className="space-y-2">
              {completedStops.map((s) => {
                const c = completionMap[s.id];
                return (
                  <div
                    key={s.id}
                    className="bg-[#0f1622] border border-[#2E7D32]/40 rounded-sm px-3 py-3 flex items-start gap-3"
                    data-testid={`completed-row-${s.id}`}
                  >
                    <div className="w-8 h-8 rounded-sm grid place-items-center bg-[#2E7D32] text-white font-bold flex-shrink-0">✓</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-white truncate">{s.business_name}</div>
                      <div className="text-xs text-white/60 truncate">{s.service_address}</div>
                      <div className="text-[11px] text-white/55 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{formatLocalTime(c?.completed_at)}</span>
                        {c?.driver_name && <span>· by {c.driver_name}</span>}
                        <span>· {s.waste_stream || "MSW"} · {s.container_size || s.dumpster_size}yd³</span>
                      </div>
                      {c?.notes && (
                        <div className="text-[11px] italic text-white/70 mt-1 line-clamp-2">{c.notes}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Issue modal (shared with tablet mode) */}
      {issueOpen && (
        <IssueReportModal
          current={current}
          issueType={issueType} setIssueType={setIssueType}
          notes={notes} setNotes={setNotes}
          photos={photos} photoError={photoError}
          onPhoto={onPhoto} removePhoto={removePhoto}
          onClose={closeIssueModal}
          onSubmit={submitIssue}
          onPreviewClick={(url) => setLightboxUrl(photoSrc(url))}
        />
      )}

      {/* Fullscreen photo lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 z-[60] bg-black/85 grid place-items-center p-4" onClick={() => setLightboxUrl("")} data-testid="photo-lightbox">
          <img src={lightboxUrl} alt="Issue evidence" className="max-h-[90vh] max-w-[95vw] rounded-sm" />
          <button className="absolute top-4 right-4 p-2 bg-white/10 text-white rounded-sm" onClick={() => setLightboxUrl("")} aria-label="Close lightbox" data-testid="photo-lightbox-close">
            <X size={20} weight="bold" />
          </button>
        </div>
      )}

      {/* Route Map overlay */}
      {mapOpen && (
        <RouteMapOverlay
          stops={stops}
          completionMap={completionMap}
          driverPos={driverPos}
          geoErr={geoErr}
          activeStopId={current?.id}
          onClose={() => setMapOpen(false)}
          onPickStop={(id) => {
            const i = stops.findIndex((s) => s.id === id);
            if (i >= 0) setIdx(i);
            setMapOpen(false);
          }}
        />
      )}

      <DriverSettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        route={data.route}
        stops={stops}
        completions={data.completions || []}
        onReloaded={load}
      />
      <DriverDisposalSheet
        open={disposalOpen}
        onClose={() => setDisposalOpen(false)}
        route={data.route}
        onSaved={async () => { toast.success("Disposal ticket submitted"); await load(); }}
      />
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div className="rounded-sm bg-white/5 border border-white/10 px-2 py-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#CFAB7A] font-bold">{label}</div>
      <div className="text-white font-heading font-bold mt-0.5">{value}</div>
    </div>
  );
}

function formatLocalTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatHours(hours) {
  if (hours == null) return "—";
  const total = Number(hours);
  if (total < 1 / 60) {
    // Sub-minute — show seconds so drivers see immediate confirmation the clock is running
    return `${Math.max(0, Math.round(total * 3600))}s`;
  }
  const h = Math.floor(total);
  const m = Math.round((total - h) * 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function RouteSessionCard({ activeSession, lastSession, busy, onStart, onFinish, elapsedTick }) {
  // elapsedTick is read to force a re-render every 30s so the live clock updates
  void elapsedTick;

  if (activeSession?.start_time) {
    const startTs = new Date(activeSession.start_time).getTime();
    const elapsedH = Math.max(0, (Date.now() - startTs) / 3600000);
    return (
      <section className="mx-4 mt-3" data-testid="route-session-active">
        <div className="bg-[#012340] text-white border-l-4 border-[#CFAB7A] rounded-sm p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#CFAB7A] flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2E7D32] animate-pulse" />
                Route In Progress
              </div>
              <div className="font-heading font-black text-2xl mt-1 tabular-nums" data-testid="route-session-elapsed">
                {formatHours(elapsedH)}
              </div>
              <div className="text-[11px] text-white/70 mt-0.5">
                Started {formatClock(activeSession.start_time)}
              </div>
            </div>
            <button
              onClick={onFinish}
              disabled={busy}
              data-testid="finish-route-btn"
              className="inline-flex items-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] px-4 py-3 font-heading font-black text-sm tracking-wide disabled:opacity-50 hover:brightness-105 transition-all"
            >
              <Flag size={16} weight="bold" /> {busy ? "Finishing…" : "Finish Route"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (lastSession?.status === "completed") {
    return (
      <section className="mx-4 mt-3" data-testid="route-session-completed">
        <div className="bg-[#0f1622] border border-[#2E7D32]/40 rounded-sm p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#7FC683] flex items-center gap-1.5">
              <CheckCircle size={12} weight="fill" /> Route Complete
            </div>
            <div className="text-white font-heading font-black text-lg mt-0.5" data-testid="route-session-hours">
              {formatHours(lastSession.total_hours)}
            </div>
            <div className="text-[11px] text-white/60 mt-0.5">
              {formatClock(lastSession.start_time)} → {formatClock(lastSession.finish_time)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-4 mt-3" data-testid="route-session-idle">
      <button
        onClick={onStart}
        disabled={busy}
        data-testid="start-route-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#2E7D32] text-white px-4 py-3.5 font-heading font-black text-sm tracking-wide disabled:opacity-50 hover:brightness-110 transition-all"
      >
        <Play size={16} weight="fill" /> {busy ? "Starting…" : "Start Route"}
      </button>
      <div className="mt-1 text-[10px] text-white/55 uppercase tracking-[0.25em] text-center">
        Tap to begin tracking your route hours
      </div>
    </section>
  );
}

function statusColor(status) {
  if (status === "completed") return "#2E7D32"; // green
  if (status === "issue") return "#F57C00"; // orange
  return "#012340"; // navy default
}

function StopMarkerGlyph({ num, status, isActive }) {
  const fill = statusColor(status);
  const ring = isActive ? "#CFAB7A" : fill;
  return (
    <div style={{
      background: fill, color: "#fff",
      width: 34, height: 34, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontFamily: "Chivo, sans-serif", fontSize: 13,
      border: `3px solid ${ring}`, boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
    }}>{status === "completed" ? "✓" : status === "issue" ? "!" : num}</div>
  );
}

function TruckMarkerGlyph() {
  return (
    <div style={{ position: "relative", width: 36, height: 36 }}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: "rgba(207,171,122,0.35)",
        animation: "rl-pulse 1.6s ease-out infinite",
      }} />
      <div style={{
        position: "absolute", inset: 6, background: "#012340",
        color: "#CFAB7A", borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "2px solid #CFAB7A", fontWeight: 800, fontSize: 11,
      }}>YOU</div>
      <style>{`@keyframes rl-pulse{0%{transform:scale(0.6);opacity:.8}100%{transform:scale(1.6);opacity:0}}`}</style>
    </div>
  );
}

function RecenterControl({ target }) {
  const map = useMap();
  if (!target || !map) return null;
  return (
    <button
      onClick={() => { map.panTo({ lat: target[0], lng: target[1] }); map.setZoom(15); }}
      data-testid="recenter-truck"
      className="absolute bottom-24 right-4 z-[1000] bg-[#012340] border border-[#CFAB7A]/40 rounded-sm p-3 shadow-md text-[#CFAB7A]"
      style={{ position: "absolute" }}
    >
      <Crosshair size={20} weight="bold" />
    </button>
  );
}

function DriverPolyline({ path }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !path || path.length < 2) return;
    const poly = new window.google.maps.Polyline({
      path: path.map(([lat, lng]) => ({ lat, lng })),
      geodesic: true,
      strokeColor: "#012340",
      strokeOpacity: 0,
      icons: [{
        icon: { path: "M 0,-1 0,1", strokeOpacity: 0.7, scale: 3 },
        offset: "0",
        repeat: "12px",
      }],
      map,
    });
    return () => poly.setMap(null);
  }, [map, JSON.stringify(path)]); // eslint-disable-line
  return null;
}

function DriverFitBounds({ points, ranOnceRef }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !points?.length) return;
    if (ranOnceRef.current) return;
    ranOnceRef.current = true;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
    map.fitBounds(bounds, 60);
  }, [map, JSON.stringify(points)]); // eslint-disable-line
  return null;
}

function RouteMapOverlay({ stops, completionMap, driverPos, geoErr, activeStopId, onClose, onPickStop }) {
  const valid = stops.filter((s) => s.lat && s.lng);
  const points = valid.map((s) => [s.lat, s.lng]);
  const allPoints = driverPos ? [...points, driverPos] : points;
  const center = driverPos
    ? { lat: driverPos[0], lng: driverPos[1] }
    : (points[0] ? { lat: points[0][0], lng: points[0][1] } : TAFT_CENTER);
  const [info, setInfo] = useState(null);
  const fitOnceRef = useRef(false);
  const counts = {
    completed: stops.filter((s) => completionMap[s.id]?.status === "completed").length,
    issue: stops.filter((s) => completionMap[s.id]?.status === "issue").length,
    pending: stops.filter((s) => !completionMap[s.id]).length,
  };

  const gm = useGoogleMapsStatus();
  const [tookTooLong, setTookTooLong] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  // Reset stale timeout/mount flags on every retry.
  useEffect(() => {
    setTookTooLong(false);
    setMapMounted(false);
  }, [retryKey]);
  // Arm a 12s "did the map ever load?" timer. If the map mounts (tiles load
  // or onIdle fires), we clear the timer and never flag it as unavailable.
  // Previously this used a stale-closure read of `mapMounted` and always fired
  // after 12s, falsely crashing the Route Map for drivers who left it open.
  useEffect(() => {
    if (mapMounted) return;
    const t = setTimeout(() => setTookTooLong(true), 12000);
    return () => clearTimeout(t);
  }, [mapMounted, retryKey]);

  const mapUnavailable = gm.apiKeyMissing || gm.authFailed || tookTooLong;

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0f1a] flex flex-col" data-testid="route-map-overlay">
      <div className="bg-[#012340] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MapTrifold size={20} weight="bold" className="text-[#CFAB7A]" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A]">Route Map</div>
            <div className="font-heading font-bold truncate">{stops.length} stops</div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-sm bg-white/10 hover:bg-white/20" data-testid="close-route-map">
          <X size={18} weight="bold" />
        </button>
      </div>

      <div className="bg-[#0f1622] border-b border-white/10 px-4 py-2.5 flex items-center gap-3 text-xs flex-shrink-0">
        <Legend color="#CFAB7A" label={`${counts.pending} pending`} />
        <Legend color="#2E7D32" label={`${counts.completed} done`} />
        <Legend color="#F57C00" label={`${counts.issue} issue`} />
        <div className="ml-auto text-[10px] uppercase tracking-[0.2em] text-white/55">
          {driverPos ? "GPS live" : geoErr ? "GPS off" : "Locating…"}
        </div>
      </div>

      <div className="relative flex-1">
        {mapUnavailable ? (
          <div className="absolute inset-0 grid place-items-center bg-[#0a0f1a] p-6" data-testid="route-map-unavailable">
            <div className="max-w-md w-full bg-[#0f1622] border border-white/10 rounded-sm p-6 text-center">
              <div className="w-14 h-14 rounded-sm bg-[#D32F2F]/15 text-[#FF8A80] grid place-items-center mx-auto mb-4">
                <Warning size={26} weight="bold" />
              </div>
              <div className="font-heading font-black text-xl mb-2">Unable to Load Route Map</div>
              <div className="text-sm text-white/70 mb-5">
                {gm.apiKeyMissing
                  ? "Google Maps isn't configured for this build."
                  : gm.authFailed
                  ? "Google rejected the map key for this device. Contact dispatch."
                  : "The map didn't respond in time. Check your connection and try again."}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setTookTooLong(false); setMapMounted(false); setRetryKey((k) => k + 1); }}
                  data-testid="route-map-retry"
                  className="px-4 py-3 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.22em] hover:brightness-105"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="route-map-return"
                  className="px-4 py-3 rounded-sm border border-white/20 text-white text-xs font-bold uppercase tracking-[0.22em] hover:bg-white/5"
                >
                  Return to Route
                </button>
              </div>
            </div>
          </div>
        ) : (
        <Map
          key={retryKey}
          defaultCenter={center}
          defaultZoom={13}
          mapId={GOOGLE_MAPS_MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          style={{ height: "100%", width: "100%" }}
          data-testid="driver-map"
          onTilesLoaded={() => setMapMounted(true)}
          onIdle={() => setMapMounted(true)}
        >
          {points.length > 1 && <DriverPolyline path={points} />}
          {valid.map((s) => {
            const status = completionMap[s.id]?.status;
            const num = stops.findIndex((x) => x.id === s.id) + 1;
            return (
              <AdvancedMarker
                key={s.id}
                position={{ lat: s.lat, lng: s.lng }}
                onClick={() => setInfo({ stop: s, num, status })}
              >
                <StopMarkerGlyph num={num} status={status} isActive={s.id === activeStopId} />
              </AdvancedMarker>
            );
          })}
          {driverPos && (
            <AdvancedMarker position={{ lat: driverPos[0], lng: driverPos[1] }}>
              <TruckMarkerGlyph />
            </AdvancedMarker>
          )}
          {info && (
            <InfoWindow
              position={{ lat: info.stop.lat, lng: info.stop.lng }}
              onCloseClick={() => setInfo(null)}
              pixelOffset={[0, -34]}
            >
              <div style={{ minWidth: 200 }}>
                <div style={{ fontWeight: 700, color: "#012340" }}>{info.num}. {info.stop.business_name}</div>
                <div style={{ fontSize: 12, color: "#5C738A" }}>{info.stop.service_address}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {info.status === "completed" ? "✓ Completed" : info.status === "issue" ? `⚠ Issue: ${issueLabel(completionMap[info.stop.id]?.issue_type)}` : "Pending"}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => { onPickStop(info.stop.id); setInfo(null); }}
                    style={{
                      background: "#012340", color: "#fff", padding: "5px 10px",
                      borderRadius: 4, fontWeight: 700, fontSize: 11, border: "none", cursor: "pointer",
                    }}
                  >
                    Open stop
                  </button>
                  <a
                    href={googleMapsDirectionsUrl({ lat: info.stop.lat, lng: info.stop.lng, address: info.stop.service_address })}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      background: "#CFAB7A", color: "#012340", padding: "5px 10px",
                      borderRadius: 4, fontWeight: 700, fontSize: 11, textDecoration: "none",
                    }}
                  >
                    ↗ Google Maps
                  </a>
                </div>
              </div>
            </InfoWindow>
          )}
          <DriverFitBounds points={allPoints} ranOnceRef={fitOnceRef} />
          <RecenterControl target={driverPos} />
        </Map>
        )}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block w-3 h-3 rounded-full border-2 border-white" style={{ background: color, boxShadow: `0 0 0 1px ${color}` }} />
      <span className="text-white font-semibold">{label}</span>    </div>
  );
}

function formatDriverDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      weekday: "long", month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
    });
  } catch { return iso; }
}

function RouteSelector({ user, options, onPick, onLogout, variant = "page", autoConfirm = true }) {
  const [routeId, setRouteId] = useState(null);
  const [serviceDate, setServiceDate] = useState(null);
  // Reset confirmation state. We deliberately keep this local to the selector
  // so it disappears the moment the driver taps Start Route.
  const [resetBusy, setResetBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  const routes = options?.routes || [];
  const loading = options === null;

  async function doResetDay() {
    if (!routeId || !serviceDate) return;
    setResetBusy(true);
    try {
      const { data } = await api.post("/driver/reset-day", {
        route_id: routeId,
        service_date: serviceDate,
      });
      toast.success(
        `Reset · ${data.completions_deleted || 0} stops cleared, session wiped. You can now re-run this day.`
      );
      setResetConfirm(false);
      // Reload the schedule so the "stops today" counts refresh.
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  }

  useEffect(() => {
    // Auto-pick the only route + today if both unambiguous
    if (!loading && routes.length === 1 && routeId == null) {
      setRouteId(routes[0].id);
    }
    if (!loading && !serviceDate && options?.service_date) {
      setServiceDate(options.service_date);
    }
  }, [loading, routes.length, options?.service_date, routeId, serviceDate]); // eslint-disable-line

  // Auto-confirm: DISABLED per user requirement (2026-02-09) — every driver
  // (single-route or multi-route, phone or tablet) must explicitly pick which
  // of the 7 days to run. This lets a driver on Orlando #201 tap Thursday to
  // run Thursday's stops without having to work around a "today" default.
  // The `autoConfirm` prop is kept as a no-op so external callers don't break.
  useEffect(() => {
    // no-op: previously auto-picked (routeId, today) when there was 1 route.
  }, [autoConfirm, loading, routes.length, routeId, serviceDate, options?.service_date]); // eslint-disable-line

  const selectedRoute = routes.find((r) => r.id === routeId);

  const isModal = variant === "modal";
  const Shell = isModal
    ? ({ children }) => (
        <div
          className="fixed inset-0 z-[55] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
          data-testid="route-selector-modal"
        >
          <div
            className="bg-[#012340] text-white w-full max-w-2xl rounded-sm border border-[#CFAB7A]/30 shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      )
    : ({ children }) => (
        <div className="min-h-screen bg-[#012340] text-white pb-12" data-testid="driver-route-selector">
          {children}
        </div>
      );

  return (
    <Shell>
      <header className={`flex items-center justify-between ${isModal ? "px-5 pt-5 pb-2" : "px-5 pt-6 pb-4"}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#CFAB7A] grid place-items-center text-[#012340]">
            <Truck size={20} weight="bold" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A]">Driver</div>
            <div className="font-heading font-bold text-lg">{user?.name}</div>
          </div>
        </div>
        <button onClick={onLogout} className="p-2 rounded-sm hover:bg-white/10" data-testid="route-selector-logout">
          <SignOut size={18} weight="bold" />
        </button>
      </header>

      <div className={isModal ? "px-5 pt-2" : "px-5 mt-4"}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">
          {isModal ? "Step 1" : "Step 1 of 2"}
        </div>
        <h1 className={`font-heading font-black tracking-tight mt-1 ${isModal ? "text-2xl" : "text-3xl"}`}>Select Route</h1>
        <p className="text-sm text-white/70 mt-1">
          {routes.length === 1
            ? "Auto-selecting your assigned route…"
            : `Pick the route you're running ${isModal ? "today" : "today (or planning ahead)"}.`}
        </p>

        <div className="mt-4 space-y-2">
          {loading && <div className="text-sm text-white/70" data-testid="route-selector-loading">Loading routes…</div>}
          {!loading && routes.length === 0 && (
            <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-sm" data-testid="route-selector-empty">
              No routes assigned to you yet. Ask dispatch.
            </div>
          )}
          {routes.map((r) => {
            const todayStops = (r.schedule || []).find((s) => s.is_today)?.stops_count || 0;
            const isPicked = routeId === r.id;
            const status = r.schedule?.[0]?.session_status;
            const vehicle = r.vehicle || r.assigned_vehicle || null;
            return (
              <button
                key={r.id}
                onClick={() => setRouteId(r.id)}
                data-testid={`route-option-${r.id}`}
                className={`w-full text-left rounded-sm border px-4 py-3 transition-all ${isPicked ? "bg-[#CFAB7A] text-[#012340] border-[#CFAB7A]" : "bg-white/5 border-white/15 hover:border-[#CFAB7A]"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className={`text-[10px] font-bold uppercase tracking-[0.25em] ${isPicked ? "text-[#012340]/70" : "text-[#CFAB7A]"}`}>Route #{r.route_number}</div>
                    <div className={`font-heading font-bold text-base mt-0.5 truncate ${isPicked ? "text-[#012340]" : "text-white"}`}>{r.route_name || "—"}</div>
                    <div className={`text-xs mt-1 ${isPicked ? "text-[#012340]/80" : "text-white/70"}`}>
                      {todayStops} stop{todayStops === 1 ? "" : "s"} today · {r.total_stop_ids} total assigned
                      {vehicle ? <> · Vehicle <span className="font-bold">{vehicle}</span></> : null}
                    </div>
                  </div>
                  {status && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                      status === "completed" ? "bg-[#2E7D32] text-white" :
                      status === "in_progress" ? "bg-[#F57C00] text-white" :
                      "bg-white/10 text-white"
                    }`}>{status === "in_progress" ? "In progress" : status}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Service-date step — always shown so drivers can run ANY of the next 7
          days (not just today). The 'Today' badge still highlights the current
          weekday so the common case is one tap away. */}
      {routeId && (
        <div className={isModal ? "px-5 mt-6" : "px-5 mt-8"} data-testid="route-selector-date-step">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">
            {isModal ? "Step 2" : "Step 2 of 2"}
          </div>
          <h2 className={`font-heading font-black tracking-tight mt-1 ${isModal ? "text-xl" : "text-2xl"}`}>Pick the day you&apos;re running</h2>
          <p className="text-sm text-white/70 mt-1">Tap any of the next 7 days — stops shown will match that exact calendar date.</p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(selectedRoute?.schedule || []).map((s) => {
              const isPicked = serviceDate === s.service_date;
              const dateLabel = formatDriverDate(s.service_date);
              const isToday = s.is_today;
              const weekday = s.weekday || "";
              return (
                <button
                  key={s.service_date}
                  onClick={() => setServiceDate(s.service_date)}
                  data-testid={`date-option-${s.service_date}`}
                  className={`text-left rounded-sm border px-4 py-3 transition-all flex items-center justify-between gap-3 ${isPicked ? "bg-[#CFAB7A] text-[#012340] border-[#CFAB7A]" : "bg-white/5 border-white/15 hover:border-[#CFAB7A]"}`}
                >
                  <div className="min-w-0">
                    <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isPicked ? "text-[#012340]/70" : isToday ? "text-[#2E7D32]" : "text-[#CFAB7A]"}`}>
                      {isToday ? `Today · ${weekday}` : weekday || "Upcoming"}
                    </div>
                    <div className={`font-heading font-bold text-sm ${isPicked ? "text-[#012340]" : "text-white"}`}>{dateLabel}</div>
                  </div>
                  <div className={`text-right text-xs ${isPicked ? "text-[#012340]/80" : "text-white/70"}`}>
                    <div className="font-mono font-bold tabular-nums">{s.stops_count}</div>
                    <div>stop{s.stops_count === 1 ? "" : "s"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={`px-5 ${isModal ? "mt-6 pb-5" : "mt-8"}`}>
        <button
          onClick={() => onPick(routeId, serviceDate)}
          disabled={!routeId || !serviceDate}
          data-testid="route-selector-confirm"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] py-3 font-heading font-black text-base tracking-wide disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition-all"
        >
          {serviceDate ? `Start Route · ${formatDriverDate(serviceDate)}` : "Pick a day to start"}
        </button>

        {/* Reset day — one-tap re-run for testing / for a day that was closed
            by mistake. Wipes every completion + the route session for the
            selected route + date so pending stops come back. Confirm-first
            so a tired driver doesn't nuke real work. */}
        {routeId && serviceDate && (
          <div className="mt-3" data-testid="route-selector-reset-wrap">
            {resetConfirm ? (
              <div className="rounded-sm border border-[#D32F2F]/40 bg-[#D32F2F]/10 px-3 py-3">
                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#D32F2F] mb-1">
                  Delete every stop on this day?
                </div>
                <div className="text-[11px] text-white/80 mb-3">
                  This wipes all completions + the route session for {formatDriverDate(serviceDate)} on this route.
                  Photos and dash-cam links tied to those stops are removed too. Cannot be undone.
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={doResetDay}
                    disabled={resetBusy}
                    data-testid="route-selector-reset-confirm"
                    className="flex-1 rounded-sm bg-[#D32F2F] text-white py-2 text-sm font-heading font-bold disabled:opacity-50"
                  >
                    {resetBusy ? "Resetting…" : "Yes, Reset This Day"}
                  </button>
                  <button
                    onClick={() => setResetConfirm(false)}
                    disabled={resetBusy}
                    data-testid="route-selector-reset-cancel"
                    className="rounded-sm border border-white/25 text-white/80 px-3 py-2 text-sm font-bold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setResetConfirm(true)}
                data-testid="route-selector-reset"
                className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 bg-transparent text-white/70 py-2 text-xs font-bold uppercase tracking-[0.2em] hover:text-white hover:border-white/40"
              >
                <ArrowsClockwise size={13} weight="bold" /> Reset This Day
              </button>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}


/**
 * Shared issue-report modal used by both the phone layout and the tablet hub.
 * All photo / GPS / submission logic stays in <DriverApp/> — this component
 * is purely a controlled form view.
 */
function IssueReportModal({
  current,
  issueType, setIssueType,
  notes, setNotes,
  photos, photoError,
  onPhoto, removePhoto,
  onClose, onSubmit,
  onPreviewClick,
}) {
  const uploading = photos.some((p) => p.status === "uploading");
  const doneCount = photos.filter((p) => p.status === "done").length;
  return (
    <div className="fixed inset-0 z-40 bg-black/70 grid place-items-end sm:place-items-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f1622] text-white w-full sm:max-w-md rounded-t-sm sm:rounded-sm border border-white/10 max-h-[90vh] overflow-y-auto" data-testid="issue-form">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-heading text-lg font-bold text-white">
            Report Issue {current?.business_name ? <span className="text-white/55 font-normal text-sm">· {current.business_name}</span> : null}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="issue-close" className="text-white/70 hover:text-white"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#CFAB7A] mb-2">Issue Type</div>
            <div className="grid grid-cols-2 gap-2">
              {ISSUES.map((i) => (
                <button
                  key={i.v}
                  type="button"
                  onClick={() => setIssueType(i.v)}
                  data-testid={`issue-${i.v}`}
                  className={`px-3 py-2.5 rounded-sm text-xs font-bold border text-left transition ${issueType === i.v ? "bg-[#CFAB7A] text-[#012340] border-[#CFAB7A]" : "bg-white/5 text-white border-white/15 hover:border-white/30"}`}
                >
                  {i.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#CFAB7A] mb-1">Notes</div>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="issue-notes"
              className="w-full rounded-sm bg-white/5 border border-white/15 text-white placeholder-white/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A] focus:border-[#CFAB7A]"
            />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#CFAB7A] mb-2 flex items-center justify-between">
              <span>Photos <span className="text-[#FF8A80] normal-case tracking-normal">* at least 1 required</span></span>
              <span className="text-white/50 normal-case tracking-normal" data-testid="issue-photo-count">{photos.length}/{MAX_ISSUE_PHOTOS}</span>
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2" data-testid="issue-photo-preview-wrap">
                {photos.map((p, i) => (
                  <IssuePhotoThumb key={p.key} photo={p} index={i} onRemove={() => removePhoto(p.key)} onOpen={() => p.url && onPreviewClick(p.url)} />
                ))}
              </div>
            )}

            {photos.length < MAX_ISSUE_PHOTOS && (
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] px-3 py-2.5 cursor-pointer text-sm font-bold" data-testid="issue-photo-camera-label">
                  <Camera size={16} weight="bold" /> {photos.length ? "Add Photo" : "Take Photo"}
                  <input type="file" accept={PHOTO_ACCEPT} capture="environment" className="hidden" onChange={onPhoto} data-testid="issue-photo" />
                </label>
                <label className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-3 py-2.5 cursor-pointer text-sm font-bold text-white hover:bg-white/5" data-testid="issue-photo-gallery-label">
                  Gallery
                  <input type="file" accept={PHOTO_ACCEPT} multiple className="hidden" onChange={onPhoto} data-testid="issue-photo-gallery" />
                </label>
              </div>
            )}

            {photoError && (
              <div className="mt-2 text-xs text-[#FF8A80] font-semibold" data-testid="issue-photo-error">{photoError}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onSubmit}
            disabled={uploading || !doneCount || !issueType}
            className="w-full rounded-sm bg-[#CFAB7A] text-[#012340] font-bold py-3 text-sm hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="issue-submit"
          >
            {uploading ? "Waiting for photos to upload…" : `Submit Issue${doneCount > 1 ? ` (${doneCount} photos)` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssuePhotoThumb({ photo, index, onRemove, onOpen }) {
  const busy = photo.status === "uploading";
  return (
    <div className="relative aspect-square rounded-sm overflow-hidden border border-white/15 bg-white/5" data-testid={`issue-photo-thumb-${index}`}>
      <img
        src={photo.url ? photoSrc(photo.url) : photo.local}
        alt={`Issue evidence ${index + 1}`}
        onClick={onOpen}
        className="w-full h-full object-cover cursor-zoom-in"
        data-testid={`issue-photo-preview-${index}`}
      />
      {busy && (
        <div className="absolute inset-0 bg-black/60 grid place-items-center text-white text-xs font-bold">
          <div className="flex flex-col items-center gap-1">
            <ArrowsClockwise size={18} weight="bold" className="animate-spin" />
            <span>{photo.pct}%</span>
          </div>
        </div>
      )}
      {!busy && (
        <span className="absolute bottom-1 left-1 bg-[#2E7D32] text-white text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm" data-testid={`issue-photo-uploaded-${index}`}>✓ Saved</span>
      )}
      {!busy && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove photo"
          className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/70 text-white hover:bg-[#D32F2F]"
          data-testid={`issue-photo-remove-${index}`}
        >
          <Trash size={12} weight="bold" />
        </button>
      )}
    </div>
  );
}
